/**
 * preview-logic.ts — Preview 渲染层的纯逻辑函数。
 *
 * 从 React 组件中提取的决策逻辑，方便单元测试覆盖。
 * 这些函数不依赖 React、Zustand 或任何 DOM API。
 */

import type {
    ContentDefinition,
    DocumentBlock,
    ForDefinition,
    ImportBinding,
    ImportDocumentSnapshot,
    ImportCachedInstanceSymbol,
    ImportCachedSymbol,
    PluginDefinition,
    PluginOutput,
} from "@magic-md/core";
import { splitScopedMemberPath } from "@magic-md/core";
import { t as translate, type LocaleParams } from "@magic-md/i18n";
export {
    contentNeedsExecution,
    findContentDefinitionInScope,
    findDefinitionInScope,
    findForDefinitionInScope,
    findOutputInScope,
    groupBlocks,
} from "./lib/document-render-logic";
import type { ScopeLookupNode } from "./lib/document-render-logic";

function tr(key: string, params?: LocaleParams): string {
    return translate("editor", key, params);
}

/* ── Types ── */

/**
 * RenderBlock 三路分派的决策结果。
 */
export type RenderTargetType = "for" | "content" | "definition" | "import-document" | "not-found";

export interface RenderTarget {
    type: RenderTargetType;
    id: string;
}

export type RenderResolutionIssueCode =
    | "magic-md/unknown-namespace"
    | "magic-md/import-document-render-disabled"
    | "magic-md/invalid-inline-import-render"
    | "magic-md/import-document-not-resolved"
    | "magic-md/import-symbol-not-exported"
    | "magic-md/import-symbol-not-resolved"
    | "magic-md/import-error"
    | "magic-md/unknown-definition";

export interface RenderResolutionIssue {
    code: RenderResolutionIssueCode;
    targetId: string;
    title: string;
    message: string;
    hint?: string;
    namespace?: string;
    canRunImport?: boolean;
    badgeSub?: string;
}

/**
 * 简化的 lookup 数据源（对应 PaneContext 中的 Maps）。
 */
export interface LookupSource {
    forDefinitions: Map<string, ForDefinition>;
    contentDefinitions: Map<string, ContentDefinition>;
    definitions: Map<string, PluginDefinition>;
    importDocuments?: Map<string, ImportDocumentSnapshot>;
}

/* ── Scope Lookup Types (pure, no React Context) ── */

/**
 * 与 ScopeContextValue 相同的纯数据结构（不含 React Context）。
 * 用于测试 scope 链查找逻辑。
 */
export interface ScopeData extends Omit<ScopeLookupNode, "parent"> {
    definitions: Map<string, PluginDefinition>;
    forDefinitions: Map<string, ForDefinition>;
    contentDefinitions: Map<string, ContentDefinition>;
    unitOutputs: Record<string, PluginOutput>;
    parent: ScopeData | null;
    depth: number;
}

/* ── RenderBlock Dispatch ── */

/**
 * 解析 ::render[id]:: 的目标类型。
 *
 * 查找优先级：for > content > definition > import-document > not-found。
 * 这是 PreviewPanel.tsx 的 RenderBlock 组件的核心决策逻辑。
 */
export function resolveRenderTarget(
    definitionId: string,
    source: LookupSource,
): RenderTarget {
    // 1. For-block?
    if (source.forDefinitions.has(definitionId)) {
        return { type: "for", id: definitionId };
    }
    // 2. Content-block?
    if (source.contentDefinitions.has(definitionId)) {
        return { type: "content", id: definitionId };
    }
    // 3. Plugin definition?
    if (source.definitions.has(definitionId)) {
        return { type: "definition", id: definitionId };
    }
    const scopedContentMember = splitScopedMemberPath(
        definitionId,
        (id) => source.contentDefinitions.has(id),
        (id) =>
            source.forDefinitions.has(id) ||
            source.contentDefinitions.has(id) ||
            source.definitions.has(id) ||
            !!source.importDocuments?.has(id),
    );
    if (scopedContentMember) {
        return { type: "content", id: definitionId };
    }
    // 4. Import document namespace?
    if (source.importDocuments?.has(definitionId)) {
        return { type: "import-document", id: definitionId };
    }
    // 5. Not found
    return { type: "not-found", id: definitionId };
}

/**
 * Preview 中的 imported public symbol（例如 `chart/days`）不是本地 unit；
 * 除非有本地 `::use`，否则它的按钮需要走 import/source 语义。
 */
export function resolveImportedSymbolNamespace(
    definitionId: string,
    imports: readonly ImportBinding[] | null | undefined,
    localUnitIds?: ReadonlySet<string>,
): string | null {
    if (localUnitIds?.has(definitionId)) return null;

    const slashIdx = definitionId.indexOf("/");
    if (slashIdx <= 0) return null;

    const namespace = definitionId.slice(0, slashIdx);
    const binding = imports?.find((imp) => imp.namespace === namespace);
    if (!binding) return null;
    if (definitionId === namespace) return null;

    const output = binding.output;
    if (!output) return namespace;

    if (Object.prototype.hasOwnProperty.call(output.importedIds ?? {}, definitionId)) {
        return namespace;
    }
    if ((output.symbols ?? []).some((sym) => sym.qualifiedId === definitionId)) {
        return namespace;
    }
    if ((output.instanceSymbols ?? []).some((sym) => sym.qualifiedId === definitionId)) {
        return namespace;
    }

    return namespace;
}

export type ImportedSourceUnitKind = "node" | "content" | "for";

export interface ImportedSymbolRunSource {
    definitionId: string;
    namespace: string;
    sourceUnitId: string;
    kind: ImportedSourceUnitKind;
}

function sourceUnitFromPublicName(
    binding: ImportBinding,
    publicName: string,
): string {
    return getImportSourceNameForPublicName(binding.rename, publicName);
}

function sourceUnitFromCachedSymbol(
    symbol: ImportCachedSymbol,
    fallback: string,
): string {
    return symbol.originalId ?? symbol.publicName ?? fallback;
}

function sourceUnitFromInstanceSymbol(
    symbol: ImportCachedInstanceSymbol,
): string {
    return symbol.sourceId;
}

/**
 * A rendered imported symbol is not a local unit unless `::use` materialized
 * it into this document. Footer Run should execute the source unit first, then
 * refresh the owning import snapshot.
 */
export function resolveImportedSymbolRunSource(
    definitionId: string,
    imports: readonly ImportBinding[] | null | undefined,
    localUnitIds?: ReadonlySet<string>,
): ImportedSymbolRunSource | null {
    if (localUnitIds?.has(definitionId)) return null;

    const slashIdx = definitionId.indexOf("/");
    if (slashIdx <= 0) return null;

    const namespace = definitionId.slice(0, slashIdx);
    const publicName = definitionId.slice(slashIdx + 1);
    const binding = imports?.find((imp) => imp.namespace === namespace);
    if (!binding || !publicName) return null;

    const fallbackSourceUnitId = sourceUnitFromPublicName(binding, publicName);
    const output = binding.output;
    if (!output) {
        return {
            definitionId,
            namespace,
            sourceUnitId: fallbackSourceUnitId,
            kind: "node",
        };
    }

    const publicSymbol = (output.symbols ?? []).find(
        (sym) => sym.qualifiedId === definitionId,
    );
    if (publicSymbol) {
        return {
            definitionId,
            namespace,
            sourceUnitId: sourceUnitFromCachedSymbol(publicSymbol, fallbackSourceUnitId),
            kind: publicSymbol.kind,
        };
    }

    const instanceSymbol = (output.instanceSymbols ?? []).find(
        (sym) => sym.qualifiedId === definitionId,
    );
    if (instanceSymbol) {
        return {
            definitionId,
            namespace,
            sourceUnitId: sourceUnitFromInstanceSymbol(instanceSymbol),
            kind: instanceSymbol.kind,
        };
    }

    if (Object.prototype.hasOwnProperty.call(output.importedIds ?? {}, definitionId)) {
        return {
            definitionId,
            namespace,
            sourceUnitId: fallbackSourceUnitId,
            kind: "node",
        };
    }

    return null;
}

export type RenderRunTarget =
    | { type: "definition"; definitionId: string }
    | {
        type: "imported-source";
        definitionId: string;
        namespace: string;
        sourceUnitId: string;
        kind: ImportedSourceUnitKind;
    }
    | { type: "import"; namespace: string }
    | { type: "scoped"; unitId: string };

export function resolveRenderRunTarget(
    definitionId: string,
    definitions: ReadonlyMap<string, PluginDefinition>,
    imports: readonly ImportBinding[] | null | undefined,
    localUnitIds?: ReadonlySet<string>,
): RenderRunTarget {
    const importedSource = resolveImportedSymbolRunSource(
        definitionId,
        imports,
        localUnitIds,
    );
    if (importedSource) {
        return { type: "imported-source", ...importedSource };
    }

    if (definitions.has(definitionId)) {
        return { type: "definition", definitionId };
    }

    const importNamespace = resolveImportedSymbolNamespace(
        definitionId,
        imports,
        localUnitIds,
    );
    if (importNamespace) {
        return { type: "import", namespace: importNamespace };
    }

    return { type: "scoped", unitId: definitionId };
}

function importSymbolExists(binding: ImportBinding, targetId: string): boolean {
    const output = binding.output;
    if (!output) return false;
    if (Object.prototype.hasOwnProperty.call(output.importedIds ?? {}, targetId)) {
        return true;
    }
    if ((output.symbols ?? []).some((sym) => sym.qualifiedId === targetId)) {
        return true;
    }
    if ((output.instanceSymbols ?? []).some((sym) => sym.qualifiedId === targetId)) {
        return true;
    }
    return false;
}

function formatExportHint(binding: ImportBinding): string | undefined {
    const exports = binding.output?.allExports?.length
        ? binding.output.allExports
        : binding.output?.symbols?.map((sym) => sym.publicName);
    if (!exports?.length) return undefined;
    const preview = exports.slice(0, 6).join(", ");
    const suffix = exports.length > 6
        ? tr("previewLogic.exportHint.more", { count: exports.length })
        : "";
    return tr("previewLogic.exportHint.current", { preview, suffix });
}

interface ImportFilterVisibilityIssue {
    filter: "pick" | "exclude";
    publicName: string;
    sourceName: string;
    visibleNames?: string[];
}

function getImportFilterVisibilityIssue(
    binding: ImportBinding,
    targetId: string,
): ImportFilterVisibilityIssue | undefined {
    const slashIdx = targetId.indexOf("/");
    if (slashIdx < 0) return undefined;

    const publicName = targetId.slice(slashIdx + 1);
    const sourceName = getImportSourceNameForPublicName(binding.rename, publicName);

    if (binding.exclude?.includes(sourceName)) {
        return {
            filter: "exclude",
            publicName,
            sourceName,
        };
    }

    if (binding.pick?.length && !binding.pick.includes(sourceName)) {
        return {
            filter: "pick",
            publicName,
            sourceName,
            visibleNames: binding.pick.map((name) => binding.rename?.[name] ?? name),
        };
    }

    return undefined;
}

function getImportSourceNameForPublicName(
    rename: Record<string, string> | undefined,
    publicName: string,
): string {
    if (!rename) return publicName;
    for (const [sourceName, renamedPublicName] of Object.entries(rename)) {
        if (renamedPublicName === publicName) return sourceName;
    }
    return publicName;
}

function formatImportFilterHint(issue: ImportFilterVisibilityIssue): string {
    if (issue.filter === "exclude") {
        return tr("previewLogic.filter.excludeHint", { name: issue.sourceName });
    }

    if (!issue.visibleNames?.length) {
        return tr("previewLogic.filter.pickEmptyHint");
    }

    const preview = issue.visibleNames.slice(0, 6).join(", ");
    const suffix = issue.visibleNames.length > 6
        ? tr("previewLogic.exportHint.more", { count: issue.visibleNames.length })
        : "";
    return tr("previewLogic.filter.pickVisibleHint", { preview, suffix });
}

export function resolveRenderResolutionIssue({
    targetId,
    imports,
    importDocuments,
    mode = "block",
}: {
    targetId: string;
    imports?: readonly ImportBinding[] | null;
    importDocuments?: ReadonlyMap<string, ImportDocumentSnapshot> | null;
    mode?: "block" | "inline";
}): RenderResolutionIssue {
    const bindingByNamespace = new Map(
        (imports ?? [])
            .filter((binding) => binding.namespace)
            .map((binding) => [binding.namespace, binding] as const),
    );

    const slashIdx = targetId.indexOf("/");
    if (slashIdx >= 0) {
        const namespace = targetId.slice(0, slashIdx);
        const publicName = targetId.slice(slashIdx + 1);
        const binding = bindingByNamespace.get(namespace);
        if (!binding) {
            return {
                code: "magic-md/unknown-namespace",
                targetId,
                namespace,
                title: tr("previewLogic.issue.unknownNamespace.title"),
                message: tr("previewLogic.issue.unknownNamespace.message", { targetId, namespace }),
                hint: tr("previewLogic.issue.unknownNamespace.hint", { namespace }),
                badgeSub: "I",
            };
        }

        const filterIssue = getImportFilterVisibilityIssue(binding, targetId);
        if (filterIssue) {
            return {
                code: "magic-md/import-symbol-not-exported",
                targetId,
                namespace,
                title: tr("previewLogic.issue.importSymbolFiltered.title"),
                message: tr("previewLogic.issue.importSymbolFiltered.message", {
                    targetId,
                    namespace,
                    filter: filterIssue.filter,
                }),
                hint: formatImportFilterHint(filterIssue),
                canRunImport: true,
                badgeSub: "I",
            };
        }

        if (binding.output?.__error) {
            return {
                code: "magic-md/import-error",
                targetId,
                namespace,
                title: tr("previewLogic.issue.importError.title"),
                message: tr("previewLogic.issue.importError.message", { namespace, message: binding.output.__error.message }),
                hint: tr("previewLogic.issue.importError.hint"),
                canRunImport: true,
                badgeSub: "I",
            };
        }

        if (!binding.output) {
            return {
                code: "magic-md/import-symbol-not-resolved",
                targetId,
                namespace,
                title: tr("previewLogic.issue.importNotResolved.title"),
                message: tr("previewLogic.issue.importNotResolved.message", { targetId, namespace }),
                hint: tr("previewLogic.issue.importNotResolved.hint"),
                canRunImport: true,
                badgeSub: "I",
            };
        }

        if (!importSymbolExists(binding, targetId)) {
            return {
                code: "magic-md/import-symbol-not-exported",
                targetId,
                namespace,
                title: tr("previewLogic.issue.importSymbolMissing.title"),
                message: tr("previewLogic.issue.importSymbolMissing.message", { targetId, namespace }),
                hint: formatExportHint(binding) ?? tr("previewLogic.issue.importSymbolMissing.hint", { publicName }),
                canRunImport: true,
                badgeSub: "I",
            };
        }

        return {
            code: "magic-md/import-symbol-not-resolved",
            targetId,
            namespace,
            title: tr("previewLogic.issue.importSymbolNotRestored.title"),
            message: tr("previewLogic.issue.importSymbolNotRestored.message", { targetId }),
            hint: tr("previewLogic.issue.importSymbolNotRestored.hint"),
            canRunImport: true,
            badgeSub: "I",
        };
    }

    const binding = bindingByNamespace.get(targetId);
    if (binding) {
        if (binding.output?.__error) {
            return {
                code: "magic-md/import-error",
                targetId,
                namespace: targetId,
                title: tr("previewLogic.issue.importError.title"),
                message: tr("previewLogic.issue.importError.message", { namespace: targetId, message: binding.output.__error.message }),
                hint: tr("previewLogic.issue.importError.hint"),
                canRunImport: true,
                badgeSub: "I",
            };
        }

        if (!binding.document) {
            return {
                code: "magic-md/import-document-render-disabled",
                targetId,
                namespace: targetId,
                title: tr("previewLogic.issue.importDocumentDisabled.title"),
                message: tr("previewLogic.issue.importDocumentDisabled.message", { targetId }),
                hint: tr("previewLogic.issue.importDocumentDisabled.hint", { targetId }),
                badgeSub: "I",
            };
        }

        if (mode === "inline") {
            return {
                code: "magic-md/invalid-inline-import-render",
                targetId,
                namespace: targetId,
                title: tr("previewLogic.issue.inlineImportDocument.title"),
                message: tr("previewLogic.issue.inlineImportDocument.message", { targetId }),
                hint: tr("previewLogic.issue.inlineImportDocument.hint"),
                badgeSub: "I",
            };
        }

        if (!importDocuments?.has(targetId)) {
            return {
                code: "magic-md/import-document-not-resolved",
                targetId,
                namespace: targetId,
                title: tr("previewLogic.issue.importDocumentNotResolved.title"),
                message: tr("previewLogic.issue.importDocumentNotResolved.message", { targetId }),
                hint: tr("previewLogic.issue.importDocumentNotResolved.hint"),
                canRunImport: true,
                badgeSub: "I",
            };
        }
    }

    return {
        code: "magic-md/unknown-definition",
        targetId,
        title: tr("previewLogic.issue.unknownDefinition.title"),
        message: tr("previewLogic.issue.unknownDefinition.message", { targetId }),
        hint: tr("previewLogic.issue.unknownDefinition.hint"),
    };
}

/* ── Document Classification ── */

/**
 * 对已解析文档中的所有 render 块进行分类统计。
 * 返回每种类型的数量，用于验证解析 → 分派的整体正确性。
 */
export function classifyRenderTargets(
    blocks: DocumentBlock[],
    source: LookupSource,
): { for: string[]; content: string[]; definition: string[]; importDocument: string[]; notFound: string[] } {
    const result = { for: [] as string[], content: [] as string[], definition: [] as string[], importDocument: [] as string[], notFound: [] as string[] };

    for (const block of blocks) {
        if (block.type === "block-render" || block.type === "inline-render") {
            const target = resolveRenderTarget(block.definitionId, source);
            switch (target.type) {
                case "for": result.for.push(target.id); break;
                case "content": result.content.push(target.id); break;
                case "definition": result.definition.push(target.id); break;
                case "import-document": result.importDocument.push(target.id); break;
                case "not-found": result.notFound.push(target.id); break;
            }
        }
    }

    return result;
}
